import { useEffect, useRef, useState } from "react";
import { Upload, Send, Eye, AlertTriangle, Cpu, X, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppConfig } from "@/context/ConfigContext";
import { PageHeader } from "@/pages/Dashboard";
import { sendVision } from "@/lib/api";
import { getEnvironment, agentVision } from "@/lib/agent";
import { toast } from "sonner";

export default function Vision() {
  const { models } = useAppConfig();
  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState("¿Qué aparece en esta imagen?");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const visionModels = models.filter((m) => (m.capabilities || []).includes("vision"));

  useEffect(() => {
    if (!model && visionModels.length > 0) setModel(visionModels[0].id);
  }, [visionModels, model]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(reader.result);
      setFileName(file.name);
      setResult(null);
    };
    reader.readAsDataURL(file); // processed locally, never uploaded to external services
  };

  const clearImage = () => {
    setImageDataUrl(null);
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const analyze = async () => {
    if (!imageDataUrl || !prompt.trim() || !model || loading) return;
    setLoading(true);
    setResult(null);
    // localhost -> backend local (construye el mensaje) ; preview/publicada -> puente del agente
    const env = getEnvironment();
    let request;
    if (env.mode === "local") {
      request = sendVision({ model, prompt: prompt.trim(), image_data_url: imageDataUrl });
    } else {
      const messages = [
        {
          role: "user",
          content: [
            { type: "text", text: prompt.trim() },
            { type: "image_url", image_url: { url: imageDataUrl } },
          ],
        },
      ];
      request = agentVision({ model, messages });
    }
    const data = await request.catch((e) => ({
      ok: false,
      status: "ERROR",
      error: e?.message || "Error de red",
      supports_vision: true,
    }));
    setResult(data);
    setLoading(false);
  };

  return (
    <div className="krai-enter">
      <PageHeader title="Visión" subtitle="Prueba de modelos multimodales · la imagen se procesa localmente" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Input side */}
        <div className="space-y-4">
          <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Imagen</span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="vision-file-input"
            />
            {!imageDataUrl ? (
              <button
                data-testid="vision-select-image"
                onClick={() => fileRef.current?.click()}
                className="mt-3 flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#404040] bg-[#0a0a0a] py-12 text-[#737373] transition-colors hover:border-neutral-500 hover:text-[#a3a3a3]"
              >
                <Upload className="h-6 w-6" />
                <span className="text-sm">Seleccionar imagen</span>
                <span className="font-mono text-[11px]">JPEG · PNG · WEBP</span>
              </button>
            ) : (
              <div className="mt-3 space-y-2">
                <div className="relative overflow-hidden rounded-md border border-[#262626] bg-[#0a0a0a]">
                  <img
                    src={imageDataUrl}
                    alt="preview"
                    data-testid="vision-preview"
                    className="max-h-72 w-full object-contain"
                  />
                  <button
                    data-testid="vision-clear-image"
                    onClick={clearImage}
                    className="absolute right-2 top-2 rounded-sm border border-[#404040] bg-[#0a0a0a]/80 p-1.5 text-[#a3a3a3] hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 font-mono text-[11px] text-[#737373]">
                  <ImageIcon className="h-3.5 w-3.5" /> {fileName}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
            <div className="space-y-1.5">
              <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">Modelo</label>
              <Select value={model} onValueChange={setModel} disabled={visionModels.length === 0}>
                <SelectTrigger data-testid="vision-model-select" className="border-[#262626] bg-[#0a0a0a] font-mono text-xs">
                  <SelectValue placeholder="Sin modelos" />
                </SelectTrigger>
                <SelectContent className="border-[#262626] bg-[#121212]">
                  {visionModels.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="font-mono text-xs">
                      {m.id}
                      {(m.capabilities || []).includes("vision") ? "  ·  vision" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {model && visionModels.length > 0 && !visionModels.some((m) => m.id === model) && (
                <p className="font-mono text-[11px] text-amber-400">
                  Este modelo podría no soportar visión.
                </p>
              )}
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">Pregunta</label>
              <Textarea
                data-testid="vision-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[80px] resize-none border-[#262626] bg-[#0a0a0a] text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
              />
            </div>

            <Button
              data-testid="vision-analyze"
              onClick={analyze}
              disabled={!imageDataUrl || !prompt.trim() || !model || loading}
              className="mt-4 w-full rounded-sm bg-white text-black hover:bg-neutral-200"
            >
              {loading ? <Cpu className="mr-2 h-4 w-4 animate-pulse" /> : <Send className="mr-2 h-4 w-4" />}
              {loading ? "Analizando…" : "Enviar al modelo"}
            </Button>
          </div>
        </div>

        {/* Result side */}
        <div className="rounded-md border border-[#262626] bg-[#121212] p-4" data-testid="vision-result">
          <div className="flex items-center gap-2 text-[#737373]">
            <Eye className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Respuesta</span>
          </div>

          {!result && !loading && (
            <div className="flex h-64 items-center justify-center text-center">
              <p className="max-w-xs text-sm text-[#737373]">
                Selecciona una imagen, escribe una pregunta y envíala al servidor local.
              </p>
            </div>
          )}

          {loading && (
            <div className="flex h-64 items-center justify-center gap-2 font-mono text-xs text-[#737373]">
              <Cpu className="h-4 w-4 animate-pulse" /> procesando imagen en el servidor local…
            </div>
          )}

          {result?.ok && (
            <div className="mt-4">
              <p className="whitespace-pre-wrap text-sm text-[#e5e5e5]">{result.content}</p>
              <div className="mt-3 font-mono text-[10px] text-[#737373]">
                {result.model} · {result.response_time_ms} ms · vision OK
              </div>
            </div>
          )}

          {result && !result.ok && (
            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-red-400">Error {result.status}</div>
                  <div className="mt-1 break-all font-mono text-sm text-red-200">{result.error}</div>
                </div>
              </div>
              {result.supports_vision === false && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <div className="font-mono text-xs text-amber-300">
                    El modelo seleccionado no parece soportar visión.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
